import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Utilidad para geocodificar usando Nominatim con proxy CORS
async function geocodeDireccion(direccion) {
  const url = `https://corsproxy.io/?https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(direccion)}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data && data.length > 0) {
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      display_name: data[0].display_name
    };
  }
  return null;
}

// Utilidad para calcular distancia Haversine (en km)
function haversine(lat1, lon1, lat2, lon2) {
  function toRad(x) { return x * Math.PI / 180; }
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function MapaAsignacionConductor({ show, onClose, direccion, fecha, conductores = [], onSelectConductor }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ubicacion, setUbicacion] = useState(null);
  const [map, setMap] = useState(null);
  const [conductoresCercanos, setConductoresCercanos] = useState([]);

  // Geocodifica la dirección y calcula distancias
  useEffect(() => {
    if (!show || !direccion) return;
    setLoading(true);
    setError(null);
    setUbicacion(null);
    setConductoresCercanos([]);
    geocodeDireccion(direccion)
      .then((loc) => {
        if (!loc) throw new Error("No se pudo geocodificar la dirección");
        setUbicacion(loc);
        // Calcular distancias
        const lista = (conductores || []).map((c) => {
          const lat = parseFloat(c.latitud || c.lat || 0);
          const lng = parseFloat(c.longitud || c.lng || 0);
          const dist = loc ? haversine(loc.lat, loc.lng, lat, lng) : null;
          return { ...c, lat, lng, distancia: dist };
        }).filter(c => c.lat && c.lng);
        // Ordenar por distancia
        lista.sort((a, b) => (a.distancia || 9999) - (b.distancia || 9999));
        setConductoresCercanos(lista);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [show, direccion, conductores]);

  // Inicializa el mapa
  useEffect(() => {
    if (!show || !ubicacion) return;
    let mapInstance = map;
    if (!mapInstance) {
      mapInstance = L.map("mapa-asignacion-conductor", {
        center: [ubicacion.lat, ubicacion.lng],
        zoom: 13,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors"
      }).addTo(mapInstance);
      setMap(mapInstance);
    } else {
      mapInstance.setView([ubicacion.lat, ubicacion.lng], 13);
      mapInstance.eachLayer((layer) => {
        if (layer instanceof L.Marker) mapInstance.removeLayer(layer);
      });
    }
    // Marcador de la dirección inicial
    L.marker([ubicacion.lat, ubicacion.lng], { icon: L.icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png", iconSize: [32, 32] }) })
      .addTo(mapInstance)
      .bindPopup(`<b>Dirección inicial</b><br>${ubicacion.display_name}`)
      .openPopup();
    // Marcadores de conductores
    conductoresCercanos.forEach((c) => {
      L.marker([c.lat, c.lng], { icon: L.icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/1946/1946429.png", iconSize: [28, 28] }) })
        .addTo(mapInstance)
        .bindPopup(`<b>${c.nombre || c.name}</b><br>Distancia: ${c.distancia ? c.distancia.toFixed(2) : "-"} km`);
    });
    // Limpieza al cerrar
    return () => {
      if (mapInstance) mapInstance.remove();
    };
    // eslint-disable-next-line
  }, [show, ubicacion, conductoresCercanos]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ver en mapa - Asignación de conductor</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 500 }}>
        {loading && <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}><Spinner animation="border" /> Cargando mapa...</div>}
        {error && <Alert variant="danger">{error}</Alert>}
        <div id="mapa-asignacion-conductor" style={{ width: "100%", height: 400, borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}></div>
        {ubicacion && conductoresCercanos.length > 0 && (
          <div className="mt-3">
            <h6>Conductores cercanos:</h6>
            <ul>
              {conductoresCercanos.slice(0, 10).map((c, i) => (
                <li key={i}>
                  <b>{c.nombre || c.name}</b> - {c.distancia ? c.distancia.toFixed(2) : "-"} km
                  {onSelectConductor && (
                    <Button size="sm" className="ms-2" variant="success" onClick={() => onSelectConductor(c)}>Seleccionar</Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
} 